import { Button, Typography } from '@mui/material'
import { Box } from '@mui/system'
import BigNumber from 'bignumber.js'
import HorizontalText from 'components/container/HorizontalText'
import { AssetData } from 'constants/assets'
import CoreModal from 'core/CoreModal'
import CoreTokenInput from 'core/CoreTokenInput'
import { useMemo, useState } from 'react'
import { isNativeTokenTerraAddress, toAsset } from 'utils/contractParse'
import RiskRatioBar from 'components/RiskRatioBar'
import { Asset } from 'interfaces/contracts/edgePool'
import { toA } from 'utils/display'
import { accountStore, oracleStore, poolStore } from 'stores'
import { poolService } from 'services/poolService'
import ValueChange from 'components/container/ValueChange'
import { handleSupplyChange } from 'utils/handleChangeValue'
import { ONE, PERCENT, ZERO } from 'constants/number'
import { buttonStateHelper } from 'utils/buttonStateHelper'
import ModalTextSpacing from 'components/container/ModalTextSpacing'
import { observer } from 'mobx-react-lite'
import styled from '@emotion/styled'
import { theme } from 'theme'
import WarningIcon from 'assets/icons-color/warning.svg'

interface Props {
  onClose: () => void
  assetData: AssetData
  sendRepay: (asset: Asset) => void
}

const RepayModal = observer(({ onClose, assetData, sendRepay }: Props) => {
  const poolAddr = poolService.poolAddress
  const {
    walletBorrowBalance,
    borrowAPY,
    walletNetAPY,
    walletBorrowingPowerUSD,
    walletCollateralValueUSD,
    walletTotalDebtValueUSD,
    marketInfo,
  } = poolStore.getMarketOverall(poolAddr, assetData.terraAddress)
  const balance = accountStore.getBalanceOf(assetData?.terraAddress)
  const { borrow_factor } = marketInfo[assetData.terraAddress].config
  const assetPrice = oracleStore.getAssetPrice(poolAddr, assetData.terraAddress)
  const [amount, setAmount] = useState<string>('')

  const [afBorrowBal, afNetAPY, afBorrowingPower] = useMemo(() => {
    if (!assetData) return [undefined, undefined]
    const amountUSD = (amount === '' ? ZERO : new BigNumber(amount))
      .times(assetPrice)
      .times(-1)

    const [_borrow, _netAPY] = handleSupplyChange({
      supply: walletBorrowBalance.times(assetPrice),
      change: amountUSD,
      netAPY: walletNetAPY,
      supplyAPY: borrowAPY.times(-1),
    })

    const s =
      _borrow &&
      `${toA(BigNumber.max(_borrow, 0).div(assetPrice))} ${assetData.symbol}`
    const n = _netAPY && `${toA(_netAPY)}%`
    const b =
      _borrow &&
      walletBorrowingPowerUSD &&
      `$${toA(
        walletBorrowingPowerUSD.minus(amountUSD.div(borrow_factor)).toFixed(2),
      )}`

    return [s, n, b]
  }, [
    assetData,
    amount,
    assetPrice,
    walletBorrowBalance,
    walletNetAPY,
    borrowAPY,
    walletBorrowingPowerUSD,
    borrow_factor,
  ])

  const newLTV = useMemo(() => {
    if (!walletTotalDebtValueUSD) return ZERO
    const amountUSD = (amount === '' ? ZERO : new BigNumber(amount)).times(
      assetPrice,
    )

    if (walletCollateralValueUSD?.eq(ZERO)) return ZERO
    return BigNumber.max(
      walletTotalDebtValueUSD
        .minus(amountUSD.div(borrow_factor))
        .div(walletCollateralValueUSD || ONE)
        .times(PERCENT),
      0,
    )
  }, [
    amount,
    assetPrice,
    borrow_factor,
    walletCollateralValueUSD,
    walletTotalDebtValueUSD,
  ])

  const handleOnSend = () => {
    sendRepay(
      toAsset(
        assetData?.terraAddress || '',
        new BigNumber(amount).times(new BigNumber(`1e${assetData?.decimals}`)),
      ),
    )
    onClose()
  }

  const debtValeUSD = walletBorrowBalance.times(assetPrice)
  const { state: btnState, disabled } = buttonStateHelper({
    amount,
    max: BigNumber.min(walletBorrowBalance, balance),
    needApprove: !isNativeTokenTerraAddress(assetData.terraAddress),
    state: {
      zeroMax: 'No outstanding debt',
      overflowAmount: 'Repay',
      actual: 'Repay',
    },
  })

  const inWalletUSD = balance?.times(assetPrice)

  const Content = (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h5">Repay</Typography>
      <br />
      <CoreTokenInput
        value={amount}
        assetDatas={[assetData]}
        onChange={(v) => setAmount(v)}
        maxValue={BigNumber.min(walletBorrowBalance.times(`1.001`), balance)}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          margin: '32px 0px 16px 0px',
        }}
      >
        <HorizontalText
          left="In wallet"
          right={`${toA(balance)} ${assetData?.symbol} ($${toA(inWalletUSD)})`}
        />
        <HorizontalText
          left="Debt"
          right={`${toA(walletBorrowBalance)} ${assetData.symbol} ($${toA(
            debtValeUSD,
          )})`}
        />
        <ModalTextSpacing
          itl={<Typography variant="body2">Total borrow balance</Typography>}
          itr={
            <ValueChange
              bf={`${toA(walletBorrowBalance)} ${assetData.symbol}`}
              af={afBorrowBal}
            />
          }
        />
        <ModalTextSpacing
          itl={<Typography variant="body2">Net APY</Typography>}
          itr={<ValueChange bf={`${toA(walletNetAPY)}%`} af={afNetAPY} />}
        />
        <ModalTextSpacing
          itl={<Typography variant="body2">Borrowing power left</Typography>}
          itr={
            <ValueChange
              bf={`$${walletBorrowingPowerUSD?.toFixed(2)}`}
              af={afBorrowingPower}
            />
          }
        />
      </Box>
      <RiskRatioBar RiskRatio={newLTV} title="Risk ratio" showFullText />
      <IncreaseMaxDebt />
      <Button
        variant="contained"
        fullWidth
        onClick={handleOnSend}
        disabled={(disabled && btnState !== 'Repay') || balance.lt(amount)} // can repay even more debt
      >
        {balance.lt(amount) ? 'Not enough wallet balance' : btnState}
      </Button>
    </Box>
  )
  return <CoreModal open content={Content} onClose={onClose} />
})

export default RepayModal

const CustomBox = styled(Box)`
  margin: 32px 0;
  background: rgba(255, 184, 0, 0.12);
  border-radius: 8px;
  color: ${theme.palette.warning.main};
  display: flex;
  align-items: center;
  padding: 24px;
  gap: 20px;
`

const IncreaseMaxDebt = () => {
  return (
    <CustomBox>
      <img src={WarningIcon} alt="WarningIcon" width={32} height={32} />
      <Typography
        variant="body2"
        fontWeight={600}
        color="warning.main"
        paragraph
        margin={0}
      >
        To ensure your debt position will be fully closed, we suggest sending
        the transaction 0.01% greater than your outstanding debt. Exceeding
        amount will be returned to your wallet
      </Typography>
    </CustomBox>
  )
}
